// Trigger window detection for `@mention` and `:emoji` autocomplete.
//
// The contenteditable editor doesn't have a canonical "caret offset"
// the way an `<input>` does — the caret is a DOM Range. We don't try
// to materialize a string offset; instead we walk back from the caret
// through the current text node looking for the trigger character.
// As long as the trigger sits in the same text node as the caret and
// the user hasn't crossed whitespace, we report a live trigger.
//
// Limitations:
//   - A trigger that crosses element boundaries (e.g., `@` inside a
//     <strong>) breaks the window. That's fine for our use cases —
//     mentions in chat composers are typed inline, not styled.
//   - A trigger immediately preceded by a non-space character (e.g.
//     `foo@bar`) does NOT open the window. Slack's behavior.

/** Trigger result emitted by `detectTrigger`. */
export interface TriggerWindow {
  /** The character that opened the window (`'@'` or `':'`). */
  readonly trigger: string;
  /** Query text typed AFTER the trigger char. May be empty. */
  readonly query: string;
  /**
   * Range covering the trigger character + the typed query. The
   * caller uses this when committing the autocomplete: replace the
   * range with the chosen mention chip / emoji glyph.
   */
  readonly range: Range;
  /** Bounding rect of the trigger character, for anchoring pickers. */
  readonly anchorRect: DOMRect | null;
}

const MAX_QUERY_LENGTH = 32;
// Query characters allowed AFTER the trigger. Whitespace closes the
// window; punctuation other than `_`/`.`/`-` does too — Slack
// behavior. Adjust here if a new char should keep the window open.
const QUERY_CHAR_RE = /[A-Za-z0-9_.-]/;

/**
 * Scan back from the caret through the current text node looking
 * for `@` (mention) or `:` (emoji shortcode). Returns the first
 * trigger that's still open (no whitespace between it and the
 * caret), preferring `@` when both are present.
 */
export function detectTrigger(
  root: HTMLElement,
  selection: Selection | null,
): TriggerWindow | null {
  if (selection === null || !selection.isCollapsed || selection.rangeCount === 0) {
    return null;
  }
  const range = selection.getRangeAt(0);
  const node = range.startContainer;
  if (node.nodeType !== Node.TEXT_NODE) return null;
  if (!root.contains(node)) return null;
  const text = node.nodeValue ?? '';
  const caret = range.startOffset;
  // Walk back from caret looking for a trigger char.
  let i = caret - 1;
  let lastAt = -1;
  let lastColon = -1;
  while (i >= 0 && caret - i <= MAX_QUERY_LENGTH + 1) {
    const ch = text[i];
    if (ch === undefined) break;
    if (ch === ' ' || ch === '\n' || ch === '\t' || ch === ' ') break;
    if (ch === '@') {
      lastAt = i;
      break;
    }
    if (ch === ':') {
      lastColon = i;
      break;
    }
    if (!QUERY_CHAR_RE.test(ch)) {
      // Any other punctuation closes the window.
      return null;
    }
    i--;
  }
  const idx = lastAt !== -1 ? lastAt : lastColon;
  if (idx === -1) return null;
  // The trigger must be at the start of the text node OR follow a
  // word boundary (space / start). Avoid `foo@bar`.
  if (idx > 0) {
    const prev = text[idx - 1];
    if (prev !== undefined && prev !== ' ' && prev !== '\n' && prev !== '\t' && prev !== ' ') {
      return null;
    }
  }
  const triggerChar = text[idx];
  if (triggerChar === undefined) return null;
  const query = text.slice(idx + 1, caret);
  const probe = node.ownerDocument!.createRange();
  probe.setStart(node, idx);
  probe.setEnd(node, caret);
  let anchorRect: DOMRect | null = null;
  try {
    // Bounding rect of the trigger character itself for picker
    // anchoring (caller uses this as the popover anchor rect).
    const charRange = node.ownerDocument!.createRange();
    charRange.setStart(node, idx);
    charRange.setEnd(node, idx + 1);
    anchorRect = charRange.getBoundingClientRect();
  } catch {
    anchorRect = null;
  }
  return { trigger: triggerChar, query, range: probe, anchorRect };
}

/**
 * Replace `range` with a mention chip span carrying `displayName`
 * + an optional `dataset.mentionId`. The chip is inserted as a
 * single inline node so the caret lands cleanly after it.
 */
export function commitMention(
  range: Range,
  displayName: string,
  id: string | undefined,
  selection: Selection | null,
): void {
  const doc = range.startContainer.ownerDocument;
  if (doc === null) return;
  const chip = doc.createElement('span');
  chip.className = 'mention-chip';
  chip.setAttribute('data-mention', 'true');
  // `contenteditable="false"` makes the chip behave as an atomic
  // unit: clicking selects it whole, single Backspace deletes it,
  // typing replaces it. Slack-style.
  chip.setAttribute('contenteditable', 'false');
  if (id !== undefined) chip.setAttribute('data-mention-id', id);
  // The visible text is `@<displayName>` so the chip reads
  // naturally even without CSS.
  chip.textContent = `@${displayName.replace(/^@/, '')}`;
  range.deleteContents();
  range.insertNode(chip);
  // Insert a trailing space and place the caret after it.
  const space = doc.createTextNode(' ');
  chip.parentNode?.insertBefore(space, chip.nextSibling);
  const after = doc.createRange();
  after.setStartAfter(space);
  after.collapse(true);
  if (selection !== null) {
    selection.removeAllRanges();
    selection.addRange(after);
  }
}

/**
 * Replace `range` with the emoji glyph. Caret lands after the glyph.
 */
export function commitEmoji(
  range: Range,
  glyph: string,
  selection: Selection | null,
): void {
  const doc = range.startContainer.ownerDocument;
  if (doc === null) return;
  const node = doc.createTextNode(glyph);
  range.deleteContents();
  range.insertNode(node);
  const after = doc.createRange();
  after.setStartAfter(node);
  after.collapse(true);
  if (selection !== null) {
    selection.removeAllRanges();
    selection.addRange(after);
  }
}
